import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { FINANCE_NAV } from './nav';

interface Gate { gate_id: string; gate_name: string; is_active: boolean; cached_balance: number; transaction_count_30d: number; }
interface Txn { id: string; transaction_id: string; type: string; amount: number; currency: string; status: string; date: string | null; }

const STATUS_COLORS: Record<string, string> = { success: 'success', pending: 'warning', failed: 'danger', cancelled: 'secondary' };

const PaymentProcessing: React.FC = () => {
    const { api } = useInternalApi();
    const [gates, setGates] = useState<Gate[]>([]);
    const [txns, setTxns] = useState<Txn[]>([]);
    const [byStatus, setByStatus] = useState<{ status: string; count: number }[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Payment Processing - Uzima Internal'; }, []);
    useEffect(() => {
        api.get('admin-dashboard/finance/payment-processing/').then(res => {
            if (res.data?.status === 'success') {
                setGates(res.data.data.gates || []);
                setTxns(res.data.data.recent_transactions || []);
                setByStatus(res.data.data.by_status || []);
            }
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InternalShell activeDashboard="finance_center" subNav={FINANCE_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faCreditCard} className="me-2 text-primary" />Payment Processing & Gateway Health</h2>
            <p className="text-muted mb-4">IndexPay gateway status and recent transaction activity</p>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    {byStatus.length > 0 && (
                        <div className="d-flex gap-2 mb-4">
                            {byStatus.map((s: any) => (
                                <span key={s.status} className={`badge bg-${STATUS_COLORS[s.status] || 'secondary'} p-2`}>{s.status}: {s.count}</span>
                            ))}
                        </div>
                    )}

                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white"><strong>Payment Gates</strong></div>
                        <div className="card-body p-0">
                            {gates.length === 0 ? (
                                <div className="text-center py-4 text-muted">No payment gates configured yet.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Gate</th><th>Status</th><th>Balance</th><th>Transactions (30d)</th></tr></thead>
                                    <tbody>
                                        {gates.map(g => (
                                            <tr key={g.gate_id}>
                                                <td>{g.gate_name}<div className="small text-muted">{g.gate_id}</div></td>
                                                <td><span className={`badge ${g.is_active ? 'bg-success' : 'bg-secondary'}`}>{g.is_active ? 'Active' : 'Inactive'}</span></td>
                                                <td>KES {g.cached_balance.toLocaleString()}</td>
                                                <td>{g.transaction_count_30d}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white"><strong>Recent Transactions</strong></div>
                        <div className="card-body p-0">
                            {txns.length === 0 ? (
                                <div className="text-center py-4 text-muted">No transactions yet.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Transaction ID</th><th>Type</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
                                    <tbody>
                                        {txns.map(t => (
                                            <tr key={t.id}>
                                                <td>{t.transaction_id}</td>
                                                <td className="text-capitalize">{t.type}</td>
                                                <td>{t.currency} {t.amount.toLocaleString()}</td>
                                                <td><span className={`badge bg-${STATUS_COLORS[t.status] || 'secondary'}`}>{t.status}</span></td>
                                                <td>{t.date ? new Date(t.date).toLocaleString() : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default PaymentProcessing;
